import React, { useEffect, useState } from "react";
import Select from "react-select";
import { FaPlus, FaTrash } from "react-icons/fa";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";


const PRODUCT_TYPE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "book", label: "Book" },
  { value: "dress", label: "Dress" },
  { value: "shoes", label: "Shoes" },
];

const ProductBundleCreate = () => {
  const navigate = useNavigate();
  const schoolId = localStorage.getItem("school_id");
  const added_by = localStorage.getItem("id");
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [variantsMap, setVariantsMap] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);


  const [form, setForm] = useState({
    choose_existing: "new",
    product_id: null,
    category_id: null,
    subcategory_id: null,
    product_name: "",
    product_type: "general",
    vendor_id: null,
  });

  const [variantForms, setVariantForms] = useState([
    {
      variant_id: null,
      variant_name: "",
      class_id: null,
      height_range: "",
      weight_range: "",
      size: "",
      cost_price: "",
      sale_price: "",
      increase_stock_qty: "",
      initial_stock_qty: "",
    },
  ]);

  useEffect(() => {
    if (location.state?.mode === "edit" && location.state?.product_id) {
      const pid = Number(location.state.product_id);

      setIsEditMode(true); // ✅ IMPORTANT

      setForm((prev) => ({
        ...prev,
        choose_existing: "existing",
        product_id: pid,
      }));
    } else {
      setIsEditMode(false); // normal flow
    }
  }, [location.state]);



  // Fetching API Data
  useEffect(() => {
    api.get("/get-classes").then((res) => setClasses(res.data.data || []));
  }, []);

  useEffect(() => {
    api.get("vendor/list").then((res) =>
      setVendors(Array.isArray(res.data.data) ? res.data.data : [])
    );
  }, []);

  useEffect(() => {
    api
      .get("/product/category/list", { headers: { "X-School-Id": schoolId } })
      .then((res) => {
        const list = res.data;
        setCategories(Array.isArray(list) ? list : list?.data ?? []);
      });
  }, []);

  useEffect(() => {
    if (!form.category_id) return setSubcategories([]);
    api.get(`/product/subcategory/list?category_id=${form.category_id}`).then((res) => {
      const list = res.data;
      setSubcategories(Array.isArray(list) ? list : list?.data ?? []);
    });
  }, [form.category_id]);

  useEffect(() => {
    api.get(`/product/bundle/list?school_id=${schoolId}`).then((res) =>
      setProducts(res.data?.data || [])
    );
  }, [schoolId]);

  useEffect(() => {
    if (form.choose_existing === "existing" && form.product_id) {
      api.get(`/product/bundle/view/${form.product_id}`).then((res) => {
        if (res.data?.status === "success" && res.data?.data?.variants) {
          const variants = res.data.data.variants;

          // ✅ ALWAYS load variants for dropdown
          setVariantsMap({ [form.product_id]: variants });

          // ✅ ONLY auto-create rows in EDIT MODE
          if (isEditMode) {
            setVariantForms(
              variants.map((v) => ({
                variant_id: v.variant_id,
                product_stock_id: v.product_stock_id,
                variant_name: v.variant_name,
                class_id: v.class_id || null,
                height_range: v.height_range || "",
                weight_range: v.weight_range || "",
                size: v.size || "",
                cost_price: v.cost_price || "",
                sale_price: v.sale_price || "",
                initial_stock_qty: v.stock_qty || "",
                increase_stock_qty: "",
              }))
            );
          }
        }
      });
    }
  }, [form.choose_existing, form.product_id, isEditMode]);

  // Handlers
  const handleSelectChange = (name, option) =>
    setForm({ ...form, [name]: option ? option.value : null });

  const handleInputChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleRadioChange = (e) => {
    setForm({ ...form, choose_existing: e.target.value, product_id: null });
    setVariantForms([
      {
        variant_id: null,
        variant_name: "",
        class_id: null,
        height_range: "",
        weight_range: "",
        size: "",
        cost_price: "",
        sale_price: "",
        initial_stock_qty: "",
        increase_stock_qty: "",
      },
    ]);
  };

  const handleVariantSelectChange = (idx, name, option) => {
    setVariantForms((prev) => {
      const copy = [...prev];
      if (name === "variant_id") {
        const productVariants = variantsMap[form.product_id] || [];
        const selected = productVariants.find((v) => v.variant_id === option?.value);
        if (selected) {
          copy[idx] = {
            ...copy[idx],
            variant_id: selected.variant_id,
            product_stock_id: selected.product_stock_id,
            variant_name: selected.variant_name,
            class_id: selected.class_id || null,
            height_range: selected.height_range || "",
            weight_range: selected.weight_range || "",
            size: selected.size || "",
            cost_price: selected.cost_price || "",
            sale_price: selected.sale_price || "",
            initial_stock_qty: selected.stock_qty || "",
            increase_stock_qty: "",
          };
        }
      }
      if (name === "class_id") {
        copy[idx].class_id = option ? option.value : null;
      }
      return copy;
    });
  };

  const handleVariantChange = (idx, name, value) =>
    setVariantForms((prev) => {
      const copy = [...prev];
      copy[idx][name] = value;
      return copy;
    });

  const addVariantRow = () =>
    setVariantForms([
      ...variantForms,
      {
        variant_id: null,
        variant_name: "",
        class_id: null,
        height_range: "",
        weight_range: "",
        size: "",
        cost_price: "",
        sale_price: "",
        initial_stock_qty: "",
        increase_stock_qty: "",
      },
    ]);

  const removeVariantRow = (idx) =>
    setVariantForms(variantForms.filter((_, i) => i !== idx));

  const resetAll = () => {
    setForm({
      choose_existing: "new",
      product_id: null,
      category_id: null,
      subcategory_id: null,
      product_name: "",
      product_type: "general",
      vendor_id: null,
    });
    setVariantForms([
      {
        variant_id: null,
        variant_name: "",
        class_id: null,
        height_range: "",
        weight_range: "",
        size: "",
        cost_price: "",
        sale_price: "",
        initial_stock_qty: "",
        increase_stock_qty: "",
      },
    ]);
  };

  // ✅ Final Submit Handler with Toast
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!schoolId) {
      toast.error("Missing School ID ❌");
      return;
    }

    // ✅ EDIT MODE SUBMIT
    if (isEditMode && form.product_id) {

      const items = variantForms
        .filter(v => v.product_stock_id)
        .map(v => ({
          product_stock_id: Number(v.product_stock_id),
          variant_name: v.variant_name || null,
          sale_price:
            v.sale_price !== "" && v.sale_price !== null
              ? Number(v.sale_price)
              : null,
          new_cost_price:
            v.cost_price !== "" && v.cost_price !== null
              ? Number(v.cost_price)
              : null,
          reason: "Product bundle edit from dashboard"
        }));

      if (!items.length) {
        toast.error("No variant selected for update");
        return;
      }

      const res = await api.post(
        "/product/bundle/edit",
        { items },
        { headers: { "X-School-Id": schoolId } }
      );

      if (res.data?.status === "success") {
        toast.success("✅ Product updated successfully");

        setTimeout(() => {
          navigate("/admin/product-bundle/list");
        }, 800); // toast দেখানোর জন্য সামান্য delay
      }
      else {
        toast.error(res.data?.message || "Update failed");
      }

      return;
    }

    // ✅ NORMAL CREATE SUBMIT
    try {
      if (form.choose_existing === "existing" && form.product_id) {
        const payload =
        {
          school_id: Number(schoolId),
          added_by: Number(added_by),
          product_id: Number(form.product_id),
          vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
          variants: variantForms.map((v) => ({
            variant_id: v.variant_id || null,
            variant_name: v.variant_name || "",
            increase_stock_qty: Number(v.increase_stock_qty || 0),
            cost_price: Number(v.cost_price || 0),
            sale_price: Number(v.sale_price || 0),
            initial_stock_qty: Number(v.initial_stock_qty || 0),
          })),
        };

        const res = await api.post(`/product/bundle/create_exiting`, payload, {
          headers: { "X-School-Id": schoolId },
        });

        if (res.data?.success || res.data?.status === "success") {
          toast.success("✅ Existing product updated successfully!");
          resetAll();
        } else {
          toast.error(res.data?.message || "Operation failed ❌");
        }
        return;
      }

      if (!form.subcategory_id || !form.product_name) {
        toast.error("⚠ Please fill product form properly!");
        return;
      }

      const payload = {
        school_id: Number(schoolId),
        added_by: Number(added_by),
        subcategory_id: Number(form.subcategory_id),
        vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
        product: {
          product_name: form.product_name,
          product_type: form.product_type,
        },
        variants: variantForms.map((v) => ({
          variant_name: v.variant_name,
          class_id: v.class_id || null,
          height_range: v.height_range || null,
          weight_range: v.weight_range || null,
          size: v.size || null,
          cost_price: Number(v.cost_price || 0),
          sale_price: Number(v.sale_price || 0),
          initial_stock_qty: Number(v.initial_stock_qty || 0),
        })),
      };

      const res = await api.post(`/product/bundle/create`, payload, {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.success || res.data?.status === "success") {
        toast.success("✅ Product & Variants created successfully!");
        resetAll();
      } else {
        toast.error(res.data?.message || "Operation failed ❌");
      }
    } catch (error) {
      toast.error("🚫 API Error: " + error?.message);
    }
  };

  const submitButtonLabel = isEditMode
    ? "Update Product"
    : form.choose_existing === "existing"
      ? "Add Existing Product"
      : "Add New Product";


  // ------------------ UI ------------------
  return (
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: "#f4f6f8" }}>
      <ToastContainer position="top-right" autoClose={2000} theme="colored" />
      <div style={{ flex: 1 }}>
        <main style={{ padding: 30, maxWidth: 1100, margin: "0px auto" }}>
          <section style={cardStyle}>
            <h2 style={titleStyle}>
              {isEditMode ? "✏️ Update Product Bundle" : "➕ Create Product Bundle"}
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Mode Switch */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 700, marginRight: 22 }}>
                  <input
                    type="radio"
                    name="choose_existing"
                    value="new"
                    checked={form.choose_existing === "new"}
                    onChange={handleRadioChange}
                    disabled={isEditMode}
                    style={{ marginRight: 7 }}
                  /> New Product Add
                </label>
                <label style={{ fontWeight: 700 }}>
                  <input
                    type="radio"
                    name="choose_existing"
                    value="existing"
                    checked={form.choose_existing === "existing"}
                    onChange={handleRadioChange}
                    disabled={isEditMode}
                    style={{ marginRight: 7 }}
                  /> Existing Product Add
                </label>
              </div>

              {/* Product Form */}
              <div style={grid2}>
                {form.choose_existing === "existing" ? (
                  <>
                    <FormGroup label="Select Product">
                      <Select
                        options={products.map((p) => ({
                          value: p.product_id || p.id,
                          label: p.product_name,
                        }))}
                        value={
                          products.find((p) => (p.product_id || p.id) === form.product_id)
                            ? {
                              value: form.product_id,
                              label: products.find(
                                (p) => (p.product_id || p.id) === form.product_id
                              ).product_name,
                            }
                            : null
                        }
                        onChange={(opt) => handleSelectChange("product_id", opt)}
                        disabled={isEditMode}
                        isClearable
                        placeholder="Select Product"
                      />
                    </FormGroup>
                    <FormGroup label="Product Type">
                      <input
                        type="text"
                        readOnly
                        style={{ ...inputStyle, background: "#e0e7ef" }}
                        value={
                          products.find((p) => (p.product_id || p.id) === form.product_id)
                            ?.product_type || ""
                        }
                      />
                    </FormGroup>
                  </>
                ) : (
                  <>
                    <FormGroup label="Category">
                      <Select
                        options={categories.map((c) => ({
                          value: c.id,
                          label: c.category_name,
                        }))}
                        value={
                          categories.find((c) => c.id === form.category_id)
                            ? {
                              value: form.category_id,
                              label: categories.find((c) => c.id === form.category_id)
                                .category_name,
                            }
                            : null
                        }
                        onChange={(opt) => handleSelectChange("category_id", opt)}
                        isClearable
                        placeholder="Select Category"
                      />
                    </FormGroup>
                    <FormGroup label="Subcategory">
                      <Select
                        options={subcategories.map((s) => ({
                          value: s.id,
                          label: s.subcategory_name,
                        }))}
                        value={
                          subcategories.find((s) => s.id === form.subcategory_id)
                            ? {
                              value: form.subcategory_id,
                              label: subcategories.find(
                                (s) => s.id === form.subcategory_id
                              ).subcategory_name,
                            }
                            : null
                        }
                        onChange={(opt) => handleSelectChange("subcategory_id", opt)}
                        isClearable
                        placeholder="Select Subcategory"
                      />
                    </FormGroup>
                    <FormGroup label="Product Name">
                      <input
                        type="text"
                        name="product_name"
                        value={form.product_name}
                        onChange={handleInputChange}
                        style={inputStyle}
                        placeholder="e.g. Class 5 Science Book"
                        required
                      />
                    </FormGroup>
                    <FormGroup label="Product Type">
                      <Select
                        options={PRODUCT_TYPE_OPTIONS}
                        value={PRODUCT_TYPE_OPTIONS.find(
                          (o) => o.value === form.product_type
                        )}
                        onChange={(opt) => handleSelectChange("product_type", opt)}
                        placeholder="Product Type"
                      />
                    </FormGroup>
                  </>
                )}
              </div>

              <FormGroup label="Vendor (optional)">
                <Select
                  options={vendors.map((v) => ({
                    value: v.id,
                    label: v.name,
                  }))}
                  value={
                    vendors.find((v) => v.id === form.vendor_id)
                      ? {
                        value: form.vendor_id,
                        label: vendors.find((v) => v.id === form.vendor_id).name,
                      }
                      : null
                  }
                  onChange={(opt) => handleSelectChange("vendor_id", opt)}
                  disabled={isEditMode}
                  isClearable
                  placeholder="Select Vendor"
                />
              </FormGroup>

              {/* Variants */}
              <div style={{ marginTop: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3 style={{ margin: "10px 0", fontWeight: 700 }}>Variants</h3>
                  {!isEditMode && (
                    <button type="button" onClick={addVariantRow} style={btnGhost}>
                      <FaPlus /> Add Row
                    </button>
                  )}

                </div>

                {variantForms.map((v, idx) => (
                  <div key={idx} style={variantRow}>
                    {form.choose_existing === "existing" ? (
                      <div style={gridAuto}>
                        <FormGroup label="Select Variant">
                          <Select
                            options={(variantsMap?.[form.product_id] || []).map((variant) => ({
                              value: variant.variant_id,
                              label: variant.variant_name,
                            }))}
                            value={
                              (variantsMap?.[form.product_id] || []).find(
                                (variant) => variant.variant_id === v.variant_id
                              )
                                ? {
                                  value: v.variant_id,
                                  label: (
                                    variantsMap?.[form.product_id] || []
                                  ).find(
                                    (variant) => variant.variant_id === v.variant_id
                                  ).variant_name,
                                }
                                : null
                            }
                            onChange={(opt) => handleVariantSelectChange(idx, "variant_id", opt)}
                            placeholder="Select Variant"
                            isClearable
                          />
                        </FormGroup>

                        <FormGroup label="Increase Stock Qty">
                          <input
                            type="number"
                            value={v.increase_stock_qty || ""}
                            onChange={(e) =>
                              handleVariantChange(idx, "increase_stock_qty", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="Qty to Add"
                          />
                        </FormGroup>

                        <FormGroup label="Cost Price">
                          <input
                            type="number"
                            value={v.cost_price || ""}
                            onChange={(e) =>
                              handleVariantChange(idx, "cost_price", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="0.00"
                          />
                        </FormGroup>

                        <FormGroup label="Sale Price">
                          <input
                            type="number"
                            value={v.sale_price || ""}
                            onChange={(e) =>
                              handleVariantChange(idx, "sale_price", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="0.00"
                          />
                        </FormGroup>
                      </div>
                    ) : (
                      <div style={gridAuto}>
                        <FormGroup label="Variant Name">
                          <input
                            type="text"
                            value={v.variant_name}
                            onChange={(e) =>
                              handleVariantChange(idx, "variant_name", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="Variant name"
                          />
                        </FormGroup>
                        {/* 🟣 Conditional Fields by Product Type */}
                        {form.product_type === "book" && (
                          <FormGroup label="Class (searchable)">
                            <Select
                              options={classes.map((cls) => ({
                                value: cls.id,
                                label: cls.class_name,
                              }))}
                              value={
                                classes.find((cls) => cls.id === v.class_id)
                                  ? {
                                    value: v.class_id,
                                    label: classes.find((cls) => cls.id === v.class_id)
                                      .class_name,
                                  }
                                  : null
                              }
                              onChange={(opt) => handleVariantSelectChange(idx, "class_id", opt)}
                              isClearable
                              placeholder="Select Class"
                            />
                          </FormGroup>
                        )}

                        {form.product_type === "dress" && (
                          <>
                            <FormGroup label="Height Range (cm)">
                              <input
                                type="text"
                                value={v.height_range}
                                onChange={(e) =>
                                  handleVariantChange(idx, "height_range", e.target.value)
                                }
                                style={inputStyle}
                                placeholder="e.g. 120–130"
                              />
                            </FormGroup>

                            <FormGroup label="Weight Range (kg)">
                              <input
                                type="text"
                                value={v.weight_range}
                                onChange={(e) =>
                                  handleVariantChange(idx, "weight_range", e.target.value)
                                }
                                style={inputStyle}
                                placeholder="e.g. 25–30"
                              />
                            </FormGroup>
                          </>
                        )}

                        {form.product_type === "shoes" && (
                          <FormGroup label="Size (UK)">
                            <input
                              type="text"
                              value={v.size}
                              onChange={(e) =>
                                handleVariantChange(idx, "size", e.target.value)
                              }
                              style={inputStyle}
                              placeholder="e.g. 7, 8, 9 UK"
                            />
                          </FormGroup>
                        )}
                        <FormGroup label="Cost Price">
                          <input
                            type="number"
                            value={v.cost_price}
                            onChange={(e) =>
                              handleVariantChange(idx, "cost_price", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="0.00"
                          />
                        </FormGroup>
                        <FormGroup label="Sale Price">
                          <input
                            type="number"
                            value={v.sale_price}
                            onChange={(e) =>
                              handleVariantChange(idx, "sale_price", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="0.00"
                          />
                        </FormGroup>
                        <FormGroup label="Initial Stock">
                          <input
                            type="number"
                            value={v.initial_stock_qty}
                            onChange={(e) =>
                              handleVariantChange(idx, "initial_stock_qty", e.target.value)
                            }
                            style={inputStyle}
                            placeholder="0"
                          />
                        </FormGroup>
                      </div>
                    )}
                    {variantForms.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVariantRow(idx)}
                        style={btnDanger}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ textAlign: "right", marginTop: 22 }}>
                <button type="submit" style={buttonStyle}>
                  {submitButtonLabel}
                </button>
              </div>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
};

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 };
const gridAuto = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: 16,
};
const variantRow = {
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  padding: 16,
  borderRadius: 12,
  position: "relative",
  marginTop: 10
};

const inputStyle = {
  width: "95%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  outline: "none",
};
const cardStyle = {
  background: "#ffffff",
  padding: 26,
  borderRadius: 14,
  boxShadow: "0 8px 30px rgba(2,6,23,.06)",
};
const titleStyle = {
  color: "#0f172a",
  marginBottom: 18,
  fontWeight: 800,
  fontSize: 22,
};
const btnGhost = {
  padding: "8px 14px",
  background: "#e2e8f0",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 6,
};
const btnDanger = {
  padding: 10,
  background: "#fee2e2",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  color: "#b91c1c",
  position: "absolute",
  top: 0,
  right: 0,
};
const buttonStyle = {
  padding: "11px 22px",
  backgroundColor: "#0ea5e9",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer",
};

const FormGroup = ({ label, children }) => (
  <div>
    <label style={{ display: "block", marginBottom: 6, color: "#334155", fontWeight: 600 }}>
      {label}
    </label>
    {children}
  </div>
);

export default ProductBundleCreate;
